import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { api } from '../api/client.ts';
import type { PlanNotice } from '../api/types.ts';
import { usePlayground } from './playground.ts';
import { useStore } from './store.tsx';

/**
 * One-time notes a person dismisses per plan (cloud/API.md, Notices). Every screen showing the same
 * note shares one answer per person and plan, so dismissing it on Home also hides it on Plan. In the
 * /try playground nothing is stored: a dismissal lasts for the visit.
 */
const answers = new Map<string, boolean>();
const loading = new Set<string>();
const listeners = new Set<() => void>();

const publish = (key: string, dismissed: boolean) => {
  answers.set(key, dismissed);
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Whether this person dismissed `notice` on the active plan: null while it loads (and if it cannot be
 * read, so a note is never shown by mistake). `dismiss` hides it at once and saves in the background.
 */
export function usePlanNotice(notice: PlanNotice): { dismissed: boolean | null; dismiss: () => void } {
  const { me, activePlanId } = useStore();
  const playground = usePlayground() !== null;
  const key = playground ? `try:${notice}` : `${me?.user.id ?? ''}:${activePlanId ?? 'primary'}:${notice}`;
  const dismissed = useSyncExternalStore(subscribe, () => answers.get(key) ?? null);

  useEffect(() => {
    if (answers.has(key) || loading.has(key)) return;
    if (playground) {
      publish(key, false);
      return;
    }
    loading.add(key);
    api
      .notices(activePlanId)
      .then(({ dismissed: list }) => {
        if (!answers.has(key)) publish(key, list.includes(notice));
      })
      .catch(() => {})
      .finally(() => loading.delete(key));
  }, [key, notice, activePlanId, playground]);

  const dismiss = useCallback(() => {
    publish(key, true);
    if (!playground) api.dismissNotice(notice, activePlanId).catch(() => {});
  }, [key, notice, activePlanId, playground]);

  return { dismissed, dismiss };
}
